import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  ref,
  watch,
  type Ref,
} from "vue";
import { gql } from "@urql/core";
import { useMutation } from "@urql/vue";

export type WorkspacePlacement = "BEFORE" | "AFTER";

export interface WorkspaceOrderItem {
  isHcpUploadWorkspace?: boolean;
  id: string;
  name: string;
  myPrivileges?: {
    mayAdministrate?: boolean | null;
  } | null;
}

export interface WorkspaceMove {
  targetId: string;
  anchorId: string;
  placement: WorkspacePlacement;
}

export interface WorkspacePlacementItem {
  isHcpUploadWorkspace?: boolean;
  id: string;
  name: string;
  parentId?: string | null;
  myPrivileges?: {
    mayAdministrate?: boolean | null;
  } | null;
  documents?: {
    totalCount: number;
  } | null;
  childWorkspaces?: {
    totalCount: number;
  } | null;
}

interface PlacementResult {
  placeWorkspace?: {
    workspaces?: Array<WorkspacePlacementItem | null> | null;
  } | null;
}

interface PlacementVariables {
  targetId: string;
  anchorId: string;
  placement: WorkspacePlacement;
}

interface MutationResult {
  data?: PlacementResult;
  error?: { message: string } | null;
}

type PlacementExecutor = (
  variables: PlacementVariables,
) => Promise<MutationResult>;

interface WorkspaceOrderingOptions<T extends WorkspaceOrderItem> {
  execute?: PlacementExecutor;
  onCanonicalOrder?: (items: T[]) => void | Promise<void>;
  refresh?: () => Promise<readonly T[] | undefined>;
}

const PlaceWorkspaceDocument = gql`
  mutation PlaceWorkspace(
    $targetId: BigInt!
    $anchorId: BigInt!
    $placement: WorkspacePlacement!
  ) {
    placeWorkspace(
      input: { targetId: $targetId, anchorId: $anchorId, placement: $placement }
    ) {
      workspaces {
        id
        name
        isHcpUploadWorkspace
        root { isHcpUploadWorkspace }
        parentId
        myPrivileges {
          mayAdministrate
        }
        documents(filter: { deletedAt: { isNull: true } }) {
          totalCount
        }
        childWorkspaces {
          totalCount
        }
      }
    }
  }
`;

export interface RootWorkspaceOrderRefreshResult {
  workspaces?: {
    nodes: Array<(WorkspaceOrderItem & { isHcpUploadWorkspace: boolean }) | null>;
  } | null;
}

export interface ChildWorkspaceOrderRefreshItem extends WorkspacePlacementItem {
  isHcpUploadWorkspace: boolean;
  root?: { isHcpUploadWorkspace: boolean } | null;
  documents: {
    totalCount: number;
  };
  childWorkspaces: {
    totalCount: number;
  };
}

export interface ChildWorkspaceOrderRefreshResult {
  workspace?: {
    childWorkspaces: {
      nodes: Array<ChildWorkspaceOrderRefreshItem | null>;
    };
  } | null;
}

export const RootWorkspaceOrderRefreshDocument = gql`
  query RootWorkspaceOrderAfterPlacement {
    workspaces(
      condition: { parentId: null }
      orderBy: [TEAM_RANK_DESC, NAME_LOWERCASE_ASC, ID_ASC]
    ) {
      nodes {
        id
        name
        isHcpUploadWorkspace
        root { isHcpUploadWorkspace }
        myPrivileges {
          mayAdministrate
        }
      }
    }
  }
`;

export const ChildWorkspaceOrderRefreshDocument = gql`
  query ChildWorkspaceOrderAfterPlacement($id: BigInt!) {
    workspace(id: $id) {
      childWorkspaces(orderBy: [TEAM_RANK_DESC, CREATED_AT_ASC, ID_ASC]) {
        nodes {
          id
          name
        isHcpUploadWorkspace
        root { isHcpUploadWorkspace }
          parentId
          myPrivileges {
            mayAdministrate
          }
          documents(filter: { deletedAt: { isNull: true } }) {
            totalCount
          }
          childWorkspaces {
            totalCount
          }
        }
      }
    }
  }
`;

export function canEditWorkspaceOrder(
  items: readonly WorkspaceOrderItem[],
): boolean {
  return (
    items.length >= 2 &&
    items.some((item) => item.myPrivileges?.mayAdministrate === true)
  );
}

export function placeWorkspaceRelative<T extends { id: string }>(
  items: readonly T[],
  move: WorkspaceMove,
): T[] {
  const sourceIndex = items.findIndex((item) => item.id === move.targetId);
  const anchorIndex = items.findIndex((item) => item.id === move.anchorId);

  if (sourceIndex < 0 || anchorIndex < 0 || move.targetId === move.anchorId) {
    return [...items];
  }

  const next = [...items];
  const [target] = next.splice(sourceIndex, 1);
  const nextAnchorIndex = next.findIndex((item) => item.id === move.anchorId);
  const insertionIndex =
    move.placement === "BEFORE" ? nextAnchorIndex : nextAnchorIndex + 1;
  next.splice(insertionIndex, 0, target);
  return next;
}

function sameOrder(
  left: readonly { id: string }[],
  right: readonly { id: string }[],
) {
  return (
    left.length === right.length &&
    left.every((item, index) => item.id === right[index]?.id)
  );
}

interface WorkspaceOrderingFingerprintItem {
  id: string;
  mayAdministrate: boolean;
}

function workspaceOrderingFingerprint(
  items: readonly WorkspaceOrderItem[],
): WorkspaceOrderingFingerprintItem[] {
  return items.map((item) => ({
    id: item.id,
    mayAdministrate: item.myPrivileges?.mayAdministrate === true,
  }));
}

function sameOrderingSource(
  items: readonly WorkspaceOrderItem[],
  fingerprint: readonly WorkspaceOrderingFingerprintItem[],
) {
  return (
    sameOrder(items, fingerprint) &&
    items.every(
      (item, index) =>
        (item.myPrivileges?.mayAdministrate === true) ===
        fingerprint[index]?.mayAdministrate,
    )
  );
}

export function useWorkspaceOrdering<T extends WorkspaceOrderItem>(
  source: Ref<readonly T[]>,
  options: WorkspaceOrderingOptions<T> = {},
) {
  const mutation = options.execute
    ? null
    : useMutation<PlacementResult, PlacementVariables>(PlaceWorkspaceDocument);
  const execute: PlacementExecutor =
    options.execute ?? ((variables) => mutation!.executeMutation(variables));

  const items = ref<T[]>([...source.value]) as Ref<T[]>;
  const editing = ref(false);
  const saving = ref(false);
  const gestureActive = ref(false);
  const status = ref<"idle" | "saving" | "saved" | "error">("idle");
  const errorMessage = ref<string | null>(null);
  const failedMove = ref<WorkspaceMove | null>(null);
  const failedTargetId = computed(() => failedMove.value?.targetId ?? null);
  let canonicalSource: WorkspaceOrderingFingerprintItem[] | null = null;
  let deferredSource: T[] | null = null;
  let deferredCanonicalRefresh: T[] | null = null;
  let canonicalRefresh: Promise<boolean> | null = null;
  let canonicalRefreshResult: WorkspaceOrderingFingerprintItem[] | null = null;
  let sourceGeneration = 0;
  let savedTimer: ReturnType<typeof setTimeout> | null = null;
  let doneRequested = false;

  const eligible = computed(() => canEditWorkspaceOrder(items.value));

  function clearSavedTimer() {
    if (savedTimer) globalThis.clearTimeout(savedTimer);
    savedTimer = null;
  }

  function acceptSource(next: readonly T[]) {
    if (saving.value || gestureActive.value) {
      deferredSource = [...next];
      return;
    }

    if (canonicalRefresh) {
      // A source emitted after this refresh started must be reconciled against
      // its result, even when it still matches the previous canonical state.
      // Ignore only the refresh result echoed by onCanonicalOrder itself.
      if (
        canonicalRefreshResult &&
        sameOrderingSource(next, canonicalRefreshResult)
      ) {
        return;
      }
      deferredSource = [...next];
      return;
    }

    if (canonicalSource) {
      if (sameOrderingSource(next, canonicalSource)) {
        deferredSource = null;
        items.value = [...next];
      } else {
        // Poll results are not attributable to their request start. Keep the
        // last explicit canonical order until another distinct refresh
        // confirms whether this mismatch is stale or a newer server order.
        deferredSource = [...next];
        void refreshCanonicalMismatch();
      }
      return;
    }

    items.value = [...next];
  }

  watch(source, (next) => {
    sourceGeneration += 1;
    acceptSource(next);
  });

  watch(eligible, (isEligible) => {
    if (!isEligible && editing.value) done();
  });

  async function publishCanonicalRefresh(refreshed: readonly T[]) {
    items.value = [...refreshed];
    try {
      await options.onCanonicalOrder?.([...refreshed]);
    } catch {
      // The explicit refresh remains authoritative for this ordering UI.
    }
  }

  function flushDeferredCanonicalRefresh() {
    if (gestureActive.value || !deferredCanonicalRefresh) return false;
    const refreshed = deferredCanonicalRefresh;
    const laterSource = deferredSource;
    deferredCanonicalRefresh = null;
    deferredSource = null;
    void publishCanonicalRefresh(refreshed);
    if (laterSource) acceptSource(laterSource);
    return true;
  }

  async function refreshCanonicalOrder(clearDeferredThroughGeneration?: number) {
    if (!options.refresh) return false;
    if (canonicalRefresh) return canonicalRefresh;
    const sourceGenerationAtStart = sourceGeneration;
    const clearableGeneration =
      clearDeferredThroughGeneration ?? sourceGenerationAtStart;

    const refresh = (async () => {
      try {
        const refreshed = await options.refresh?.();
        if (!refreshed) return false;

        canonicalRefreshResult = workspaceOrderingFingerprint(refreshed);
        canonicalSource = canonicalRefreshResult;
        if (
          sourceGeneration === sourceGenerationAtStart &&
          sourceGeneration <= clearableGeneration
        ) {
          deferredSource = null;
        }
        if (!refreshed.length && editing.value) done();
        if (gestureActive.value) {
          deferredCanonicalRefresh = [...refreshed];
        } else {
          await publishCanonicalRefresh(refreshed);
        }
        return true;
      } catch {
        return false;
      }
    })();

    canonicalRefresh = refresh;
    try {
      return await refresh;
    } finally {
      if (canonicalRefresh === refresh) {
        canonicalRefresh = null;
        canonicalRefreshResult = null;
      }
    }
  }

  async function refreshCanonicalMismatch() {
    if (!canonicalSource || !deferredSource || canonicalRefresh) return;
    const mismatchAtStart = workspaceOrderingFingerprint(deferredSource);
    const refreshed = await refreshCanonicalOrder();
    if (!refreshed) {
      if (
        !saving.value &&
        !gestureActive.value &&
        deferredSource &&
        !sameOrderingSource(deferredSource, mismatchAtStart)
      ) {
        void refreshCanonicalMismatch();
      }
      return;
    }
    if (
      !saving.value &&
      !gestureActive.value &&
      deferredSource
    ) {
      const deferred = deferredSource;
      deferredSource = null;
      if (!sameOrderingSource(deferred, mismatchAtStart)) {
        acceptSource(deferred);
      }
    }
  }

  function start() {
    const current = items.value;
    if (!canEditWorkspaceOrder(current)) return false;
    items.value = [...current];
    editing.value = true;
    status.value = "idle";
    errorMessage.value = null;
    failedMove.value = null;
    return true;
  }

  function done() {
    if (saving.value || gestureActive.value) {
      doneRequested = true;
      return false;
    }
    doneRequested = false;
    editing.value = false;
    gestureActive.value = false;
    status.value = "idle";
    errorMessage.value = null;
    failedMove.value = null;
    if (flushDeferredCanonicalRefresh()) return true;
    if (deferredSource) {
      const deferred = deferredSource;
      deferredSource = null;
      acceptSource(deferred);
    }
    return true;
  }

  function finishRequestedDone() {
    if (doneRequested && !saving.value && !gestureActive.value) done();
  }

  function setGestureActive(active: boolean) {
    gestureActive.value = active;
    if (!active) {
      const flushedCanonical = flushDeferredCanonicalRefresh();
      if (!flushedCanonical && !saving.value && deferredSource) {
        const deferred = deferredSource;
        deferredSource = null;
        acceptSource(deferred);
      }
      finishRequestedDone();
    }
  }

  function reconcileReturned(
    returned: readonly WorkspacePlacementItem[],
    optimistic: readonly T[],
  ): { items: T[]; canonicalSource: WorkspaceOrderingFingerprintItem[] } {
    const known = new Map<string, T>();
    for (const item of [
      ...source.value,
      ...(deferredSource ?? []),
      ...items.value,
      ...optimistic,
    ]) {
      known.set(item.id, item);
    }

    const reconciled: T[] = [];
    for (const workspace of returned) {
      const existing = known.get(workspace.id);
      const completeUnknown =
        Object.hasOwn(workspace, "parentId") &&
        workspace.documents != null &&
        workspace.childWorkspaces != null;
      if (!existing && !completeUnknown) continue;
      reconciled.push({ ...(existing ?? {}), ...workspace } as T);
    }

    return {
      items: reconciled,
      canonicalSource: workspaceOrderingFingerprint(returned),
    };
  }

  async function move(request: WorkspaceMove) {
    if (saving.value || !editing.value) return false;
    if (canonicalRefresh) await canonicalRefresh;
    if (saving.value || !editing.value) return false;

    const target = items.value.find((item) => item.id === request.targetId);
    if (target?.myPrivileges?.mayAdministrate !== true) return false;

    const before = [...items.value];
    const optimistic = placeWorkspaceRelative(before, request);
    if (sameOrder(before, optimistic)) return false;

    clearSavedTimer();
    errorMessage.value = null;
    failedMove.value = null;
    items.value = optimistic;
    saving.value = true;
    status.value = "saving";
    const sourceGenerationBeforeMutation = sourceGeneration;

    try {
      const result = await execute(request);
      if (result.error) throw new Error(result.error.message);

      const returned =
        result.data?.placeWorkspace?.workspaces?.filter(
          (workspace): workspace is WorkspacePlacementItem => workspace != null && !workspace.isHcpUploadWorkspace,
        ) ?? [];
      const reconciled = returned.length
        ? reconcileReturned(returned, optimistic)
        : {
            items: optimistic,
            canonicalSource: workspaceOrderingFingerprint(optimistic),
          };
      const canonical = reconciled.items;

      items.value = canonical;
      canonicalSource = reconciled.canonicalSource;
      try {
        await options.onCanonicalOrder?.(canonical);
      } catch {
        // The placement already committed. Keep the returned canonical order;
        // the guarded network refresh below will repair local consumers.
      }
      // A failed reconciliation fetch must not roll back a committed move.
      // The guard remains active and a later mismatching poll retries this
      // distinct refresh instead of being accepted without attribution.
      await refreshCanonicalOrder(sourceGenerationBeforeMutation);
      saving.value = false;
      status.value = "saved";
      if (deferredSource) {
        const deferred = deferredSource;
        deferredSource = null;
        acceptSource(deferred);
      }
      finishRequestedDone();
      savedTimer = globalThis.setTimeout(() => {
        if (status.value === "saved") status.value = "idle";
      }, 1600);
      return true;
    } catch (error) {
      items.value = before;
      saving.value = false;
      status.value = "error";
      errorMessage.value =
        error instanceof Error
          ? error.message
          : "Workspace order could not be saved";
      failedMove.value = request;
      if (deferredSource) {
        const deferred = deferredSource;
        deferredSource = null;
        acceptSource(deferred);
      }
      finishRequestedDone();
      return false;
    }
  }

  async function retry() {
    if (canonicalRefresh) await canonicalRefresh;
    if (!failedMove.value) return false;
    const request = { ...failedMove.value };
    const target = items.value.find((item) => item.id === request.targetId);
    const anchor = items.value.find((item) => item.id === request.anchorId);
    if (!target || !anchor || target.myPrivileges?.mayAdministrate !== true) {
      failedMove.value = null;
      errorMessage.value = null;
      status.value = "idle";
      return false;
    }
    if (sameOrder(items.value, placeWorkspaceRelative(items.value, request))) {
      clearSavedTimer();
      failedMove.value = null;
      errorMessage.value = null;
      status.value = "saved";
      savedTimer = globalThis.setTimeout(() => {
        if (status.value === "saved") status.value = "idle";
      }, 1600);
      return true;
    }
    return move(request);
  }

  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      clearSavedTimer();
    });
  }

  return {
    items,
    editing,
    saving,
    gestureActive,
    status,
    errorMessage,
    failedTargetId,
    eligible,
    acceptSource,
    start,
    done,
    move,
    retry,
    setGestureActive,
  };
}
